import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { IMAGES, SITE, buildEnquiryMailto } from "../../data/content";

const HEADLINE = ["Learn Arabic", "the way it's"];

const STATS = [
  ["100%", "native tutors"],
  ["90–120", "minute lessons"],
  ["1-to-1", "& small group"],
  ["Any time", "flexible schedule"],
];

export const Hero = () => {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, -48]);
  const yWord = useTransform(scrollY, [0, 700], [0, 40]);

  return (
    <section id="top" className="relative overflow-hidden pb-16 pt-32 md:pb-24 md:pt-40">
      <motion.span
        style={{ y: yWord }}
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-24 hidden select-none font-arabic text-[30rem] leading-none text-pine/[0.045] lg:block"
      >
        ع
      </motion.span>

      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="eyebrow"
          >
            Engineers Way, Wembley Park HA9 0FJ · Native Arabic instruction
          </motion.p>

          <h1 className="mt-6 font-serif text-5xl leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-7xl">
            {HEADLINE.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block italic text-pine"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.41, ease: [0.22, 1, 0.36, 1] }}
              >
                really spoken.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-ink-soft md:text-lg"
          >
            One-to-one and group lessons in Modern Standard Arabic and Levantine, Gulf and
            Egyptian dialects — taught by native, qualified, experienced tutors at Wembley
            Park or online. Every lesson runs 90 minutes to 2 hours.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.68 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-enquire-cta"
              href={buildEnquiryMailto()}
              className="btn-primary"
            >
              Request lessons by email
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <a
              data-testid="hero-whatsapp-cta"
              href={SITE.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost"
            >
              <MessageCircle className="h-4 w-4" />
              Chat on WhatsApp
            </a>
          </motion.div>

          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-12 grid grid-cols-2 gap-6 border-t border-line pt-7 sm:grid-cols-4"
          >
            {STATS.map(([big, small]) => (
              <div key={small}>
                <dt className="font-serif text-2xl text-pine">{big}</dt>
                <dd className="mt-1 text-xs uppercase tracking-wider text-ink-mute">{small}</dd>
              </div>
            ))}
          </motion.dl>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: yImg }}
            className="relative"
          >
            <div className="overflow-hidden rounded-b-3xl rounded-t-[999px] border border-line shadow-[0_32px_64px_-32px_rgba(10,35,28,0.35)]">
              <img
                src={IMAGES.hero}
                alt="Arabic calligraphy"
                className="h-[420px] w-full object-cover md:h-[540px]"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="absolute -left-4 bottom-10 max-w-[240px] rounded-2xl border border-gold/40 bg-surface p-5 shadow-xl md:-left-10"
            >
              <p className="font-arabic text-2xl leading-none text-pine">الشامية</p>
              <p className="mt-2 text-xs font-semibold text-ink">Levantine — our flagship dialect</p>
              <p className="mt-1 text-[11px] leading-relaxed text-ink-mute">
                The main preferred dialect we teach, every day.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
